import { Link } from 'react-router-dom'

import {
  Alert,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  SkeletonRows,
  mutedTextClass,
} from '@/components/layout/ui'
import { NewProjectButton } from '@/features/project'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { formatRelative, titleCase } from '@/lib/format'
import { isPaidPlan } from '@/lib/permissions'
import { useInvitationInbox, useMembers, useProjects } from '@/lib/queries'
import { ProjectIconTile } from '@/features/project-settings'
import { v2Api } from '@/features/issue'
import { projectRef } from '@/features/issue/useWorkspace'
import { useSession } from '@/lib/session'
import { useWorkspace } from '@/lib/workspace'

/**
 * `/{slug}` — the workspace overview.
 *
 * A short answer to "what is in here and what can I open next": the counters
 * across the top, then the projects themselves. Everything on this page is
 * scoped by the workspace in the URL, and each query is keyed by that slug so
 * switching workspaces cannot serve the previous one's numbers.
 */
export default function AppHome() {
  const workspace = useWorkspace()
  const { user } = useSession()
  const projects = useProjects(workspace.slug, workspace.membership.id)
  const members = useMembers(workspace.slug)
  const inbox = useInvitationInbox()

  const issueTotal = (projects.data ?? []).reduce(
    (total, project) => total + (project.tasks_count ?? 0),
    0,
  )
  const plan = workspace.membership.plan
  const isPaid = isPaidPlan(plan)

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <PageHeader
        title={workspace.membership.name}
        description={
          user ? `Signed in as ${user.name}. ${workspace.isPersonal ? 'Personal workspace.' : 'Team workspace.'}` : undefined
        }
        action={<Badge tone={isPaid ? 'brand' : 'neutral'}>{titleCase(plan)}</Badge>}
      />

      {inbox.data && inbox.data.length > 0 ? (
        <Alert tone="info" className="mb-6" title="You have a pending invitation">
          <Link to="/invitations" className="font-medium underline">
            Review {inbox.data.length === 1 ? 'it' : `all ${inbox.data.length}`}
          </Link>
        </Alert>
      ) : null}

      <dl className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Projects" value={projects.data?.length} pending={projects.isPending} />
        <Stat label="Issues" value={issueTotal} pending={projects.isPending} />
        <Stat label="Members" value={members.data?.total} pending={members.isPending} />
        <Stat
          label="Seats billed"
          value={members.data?.seats}
          pending={members.isPending}
          hint="Viewers are free"
        />
      </dl>

      <Card>
        <CardHeader
          title="Projects"
          description="Everything in this workspace."
          action={
            <div className="flex items-center gap-3">
              <Link
                to={workspace.path('/settings/members')}
                className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                Manage access
              </Link>
              <NewProjectButton size="sm" />
            </div>
          }
        />

        {projects.isPending ? (
          <div className="p-5">
            <SkeletonRows rows={3} />
          </div>
        ) : projects.isError ? (
          <div className="p-5">
            <Alert tone="danger">{messageFor(projects.error)}</Alert>
          </div>
        ) : projects.data.length === 0 ? (
          <EmptyState
            title="No projects yet"
            // Not "created on the iPad": shipping iPads talk to
            // tasklypro.app/api, which .htaccess routes to Laravel, while this
            // app reads the Go API's own database at tasklypro.app/go. Nothing
            // made on the iPad reaches this list today.
            description="A project is where the board, backlog and sprints live. Create one to get started — anything an agent creates on this account appears here too."
            action={<NewProjectButton variant="primary" />}
          />
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-ink-800">
            {projects.data.map((project) => (
              <li key={project.id}>
                <Link
                  to={workspace.path(`/projects/${projectRef(project)}`)}
                  className="flex items-center gap-4 px-5 py-3.5 transition hover:bg-ink-50 dark:hover:bg-ink-800/60"
                >
                  <ProjectIconTile
                    icon={project.icon ?? ''}
                    color={project.color_name ?? ''}
                    size="sm"
                    logoUrl={
                      project.has_logo
                        ? v2Api.projectLogoUrl(workspace.slug, projectRef(project))
                        : null
                    }
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">{project.name}</span>
                      {project.is_default ? <Badge>Default</Badge> : null}
                    </span>
                    <span className={cn('mt-0.5 block truncate text-xs', mutedTextClass)}>
                      {project.description || `Updated ${formatRelative(project.updated_at)}`}
                    </span>
                  </span>
                  <span className={cn('shrink-0 text-xs tabular-nums', mutedTextClass)}>
                    {project.tasks_count ?? 0} issues
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {!isPaid ? (
        <Card className="mt-6 p-5">
          <h2 className="text-sm font-semibold tracking-tight">Upgrade this workspace</h2>
          <p className={cn('mt-1 text-sm', mutedTextClass)}>
            More seats, sprints, reports, custom board columns and unlimited projects — one flat
            price for the whole workspace.
          </p>
          <Link
            to={workspace.path('/settings/billing')}
            className="mt-4 inline-flex h-10 items-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            See plans
          </Link>
        </Card>
      ) : null}
    </div>
  )
}

function Stat({
  label,
  value,
  pending,
  hint,
}: {
  label: string
  value: number | undefined
  pending: boolean
  hint?: string
}) {
  return (
    <div className="rounded-card border border-ink-200 bg-white px-4 py-3 dark:border-ink-800 dark:bg-ink-900">
      <dt className={cn('text-xs', mutedTextClass)}>{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">
        {pending ? <span className="text-ink-300 dark:text-ink-700">—</span> : (value ?? 0)}
      </dd>
      {hint ? <p className="mt-0.5 text-[11px] text-ink-400">{hint}</p> : null}
    </div>
  )
}
